import React, { useCallback, useMemo, useState } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'
import ShortId from 'shortid'
import Dayjs from 'dayjs'

import Box from 'components_new/atoms/Box'
import DatePicker from 'components_new/atoms/DatePicker'

import Node, { InputType } from 'components_new/molecules/Node'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FilterContainer from 'components_new/molecules/Node/Filter'
import NameField from 'components_new/molecules/Node/NameField'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import DataType from 'types/transformation/enums/DataType'
import {
  FilterCondition,
  ReplaceNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import {
  pruneRef,
  useInputSchema,
  useIsInvalid,
  usePruneRefs,
  useColumnOrigins
} from '../hooks'

interface ReplaceNodeProps {
  id: string
  data: ReplaceNode
  selected: boolean
}

/** Two-segment pill toggle matching the board's segmented-toggle style. */
const SegmentedToggle = (props: {
  value: string
  options: { id: string; label: string }[]
  onChange: (id: string) => void
}) => (
  <Box
    sx={{
      backgroundColor: BOARD.chipBackground,
      borderRadius: '8px',
      display: 'flex',
      gap: '2px',
      p: '2px'
    }}
  >
    {props.options.map((option) => {
      const active = props.value === option.id

      return (
        <Box
          key={option.id}
          role="button"
          onClick={() => props.onChange(option.id)}
          sx={{
            backgroundColor: active ? 'common.white' : 'transparent',
            borderRadius: '6px',
            boxShadow: active ? '0 1px 2px rgba(16, 24, 40, 0.08)' : 'none',
            color: active ? BOARD.textPrimary : BOARD.textMuted,
            cursor: 'pointer',
            flex: 1,
            fontFamily: BOARD.fontBody,
            fontSize: '12px',
            fontWeight: 600,
            py: '4px',
            textAlign: 'center'
          }}
        >
          {option.label}
        </Box>
      )
    })}
  </Box>
)

/** Borderless text input with a green focus ring. */
const ValueInput = (props: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) => (
  <Box
    sx={{
      backgroundColor: BOARD.rowBackground,
      border: '1px solid transparent',
      borderRadius: '10px',
      '&:focus-within': {
        backgroundColor: 'common.white',
        borderColor: BOARD.signalGreen
      }
    }}
  >
    <input
      className="nodrag ignore-locked-mode"
      onChange={(event) => props.onChange(event.target.value)}
      placeholder={props.placeholder}
      value={props.value}
      style={{
        background: 'none',
        border: 'none',
        boxSizing: 'border-box',
        color: BOARD.textPrimary,
        fontFamily: BOARD.fontBody,
        fontSize: '13px',
        outline: 'none',
        padding: '9px 11px',
        width: '100%'
      }}
    />
  </Box>
)

const Replace = (props: ReplaceNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const [useColumn, setUseColumn] = useState(!!data.replace_column)
  const [createNewColumn, setCreateNewColumn] = useState(!!data.new_column)

  const selectedColumn = useMemo(
    () => inputSchema.find((col) => col.id === data.left_column),
    [inputSchema, data.left_column]
  )

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // Replacement column must match the target column's type.
  const replaceFieldOptions = useMemo(
    () =>
      selectedColumn
        ? fieldOptions.filter((option) => option.type === selectedColumn.type)
        : [],
    [fieldOptions, selectedColumn]
  )

  const isDateColumn =
    selectedColumn?.type === DataType.DATE ||
    selectedColumn?.type === DataType.TIMESTAMP

  // input schema has changed, validate references
  usePruneRefs<ReplaceNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, inputSchema),
      replace_column: pruneRef(nodeData.replace_column, inputSchema),
      filter_conditions: nodeData.filter_conditions.map((fc) => ({
        ...fc,
        left_column: pruneRef(fc.left_column, inputSchema),
        right_column: pruneRef(fc.right_column, inputSchema)
      }))
    }),
    [inputSchema]
  )

  const patch = useCallback(
    (partial: Partial<ReplaceNode>) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: { ...(node.data as ReplaceNode), ...partial }
              }
            : node
        )
      )
    },
    [setNodes, id]
  )

  const handleSelectLeftColumn = useCallback(
    (columnId: string) => {
      // reset replacement values when the target column changes
      patch({
        left_column: columnId,
        replace_column: null,
        replace_value: null
      })
    },
    [patch]
  )

  const handleToggleUseColumn = useCallback(
    (kind: string) => {
      const nextUseColumn = kind === 'column'

      setUseColumn(nextUseColumn)
      patch({
        replace_column: nextUseColumn ? data.replace_column : null,
        replace_value: nextUseColumn ? null : data.replace_value
      })
    },
    [patch, data.replace_column, data.replace_value]
  )

  const handleToggleNewColumn = useCallback(
    (kind: string) => {
      const nextCreate = kind === 'new'

      setCreateNewColumn(nextCreate)

      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const newNodeData = cloneDeep(node.data) as ReplaceNode

          newNodeData.new_column = nextCreate
            ? {
                id: ShortId.generate(),
                name: `replaced_${selectedColumn?.name || 'column'}`,
                reference_node: id,
                reference_node_index: node.data.index,
                type: selectedColumn?.type || DataType.STRING
              }
            : null

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id, selectedColumn]
  )

  const handleChangeNewColumnName = useCallback(
    (name: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const newNodeData = cloneDeep(node.data) as ReplaceNode

          if (newNodeData.new_column) {
            newNodeData.new_column.name = name
          }

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id]
  )

  const handleChangeFilterConditions = useCallback(
    (nextConditions: FilterCondition[]) => {
      patch({
        filter_conditions: nextConditions.map((condition, index) => ({
          ...condition,
          index
        }))
      })
    },
    [patch]
  )

  return (
    <Node
      error={isInvalid}
      icon="SwapHorizOutlined"
      inputType={InputType.SINGLE}
      title={'Replace'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.REPLACE}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '320px'
        }}
      >
        <Caption>Målattribut</Caption>
        <FieldSelect
          value={data.left_column}
          options={fieldOptions}
          onChange={handleSelectLeftColumn}
          placeholder="Välj attribut"
        />

        {data.left_column ? (
          <>
            <Box sx={{ mt: 0.5 }}>
              <Caption>Ersätt med</Caption>
            </Box>
            <SegmentedToggle
              value={useColumn ? 'column' : 'value'}
              options={[
                { id: 'value', label: 'Värde' },
                { id: 'column', label: 'Kolumn' }
              ]}
              onChange={handleToggleUseColumn}
            />
            {useColumn ? (
              <FieldSelect
                value={data.replace_column}
                options={replaceFieldOptions}
                onChange={(columnId) => patch({ replace_column: columnId })}
                placeholder="Välj kolumn"
              />
            ) : isDateColumn ? (
              <DatePicker
                value={data.replace_value}
                onChange={(value) =>
                  patch({
                    replace_value: value
                      ? Dayjs(value).format('YYYY-MM-DD')
                      : null
                  })
                }
                views={['year', 'month', 'day']}
              />
            ) : (
              <ValueInput
                value={data.replace_value || ''}
                onChange={(value) => patch({ replace_value: value })}
                placeholder="Ersättningsvärde"
              />
            )}

            <Box sx={{ mt: 0.5 }}>
              <Caption>Destination</Caption>
            </Box>
            <SegmentedToggle
              value={createNewColumn ? 'new' : 'same'}
              options={[
                { id: 'same', label: 'Samma kolumn' },
                { id: 'new', label: 'Ny kolumn' }
              ]}
              onChange={handleToggleNewColumn}
            />
            {createNewColumn && data.new_column ? (
              <NameField
                value={data.new_column.name}
                onChange={handleChangeNewColumnName}
                placeholder="Kolumnnamn"
              />
            ) : null}

            <Box sx={{ mt: 0.5 }}>
              <Caption>Villkor (valfritt)</Caption>
            </Box>
            <FilterContainer
              value={data.filter_conditions}
              onChange={handleChangeFilterConditions}
              columns={fieldOptions}
            />
          </>
        ) : null}
      </Box>
    </Node>
  )
}

export default Replace
